import { adjacentFrameTime } from "../../engine/frame-timestamps";
import type { FramePrediction, Rect, TrackingPrediction } from "../contracts";
import type { InspectorSettings } from "./controller";
import type { TrackingInspectorRow, TrackingInspectorSnapshot } from "./status";

export { frameSeekTime as getFrameSeekTime } from "../../engine/frame-timestamps";

export const formatConfidence = (value: number | null) => (value === null ? "—" : `${Math.round(value * 100)}%`);

/** Step from the containing presentation frame; never infer a nominal frame rate. */
export function getFrameStep(row: TrackingInspectorRow, currentTime: number, direction: -1 | 1): number | undefined {
  return row.frameTimes ? adjacentFrameTime(row.frameTimes, currentTime, direction) : undefined;
}

function redact(text: string): string {
  return text
    .replace(/\b(?:https?|blob|data|file):[^\s"'<>]+/gi, "[redacted URL]")
    .replace(/(^|[\s("'=])\/[^\s"'<>]+/g, "$1[redacted path]");
}

function box(rect: Rect) {
  return { x: rect.x, y: rect.y, width: rect.width, height: rect.height };
}

function diagnostics(values: Record<string, number>) {
  return Object.fromEntries(
    Object.entries(values)
      .filter(([, value]) => typeof value === "number" && Number.isFinite(value))
      .map(([key, value]) => [redact(key), value]),
  );
}

function prediction(value: TrackingPrediction | null) {
  return value
    ? { box: box(value.box), confidence: value.confidence, diagnostics: diagnostics(value.diagnostics) }
    : null;
}

function frame(value?: FramePrediction) {
  return value
    ? {
        timestamp: value.timestamp,
        duration: value.duration,
        box: value.box ? box(value.box) : null,
        sparse: prediction(value.sparse),
        nano: prediction(value.nano),
        confidence: value.confidence,
        method: value.method,
        recovery: value.recovery
          ? {
              anchorTime: value.recovery.anchorTime,
              requestId: value.recovery.requestId,
              provider: redact(value.recovery.provider),
            }
          : undefined,
        diagnostics: diagnostics(value.diagnostics),
      }
    : undefined;
}

/** Export only explicit diagnostic fields, with URLs and media paths scrubbed from messages. */
export function serializeTrackingDiagnostics(
  snapshot: TrackingInspectorSnapshot,
  row: TrackingInspectorRow,
  activeSettings: InspectorSettings,
): string {
  return JSON.stringify(
    {
      schemaVersion: 1,
      currentTime: snapshot.currentTime,
      summary: redact(snapshot.summary),
      counts: {
        total: snapshot.counts.total,
        ready: snapshot.counts.ready,
        processing: snapshot.counts.processing,
        pending: snapshot.counts.pending,
        failed: snapshot.counts.failed,
      },
      busy: snapshot.busy,
      workerCount: snapshot.workerCount,
      workerError: snapshot.workerError ? redact(snapshot.workerError) : undefined,
      recovery: snapshot.recovery
        ? {
            enabled: snapshot.recovery.enabled,
            available: snapshot.recovery.available,
            providerLabel: snapshot.recovery.providerLabel ? redact(snapshot.recovery.providerLabel) : undefined,
            requestsUsed: snapshot.recovery.requestsUsed,
            maxRequests: snapshot.recovery.maxRequests,
            concurrency: snapshot.recovery.concurrency,
            activeRequests: snapshot.recovery.activeRequests,
            outcomes: snapshot.recovery.outcomes,
            interval: row.recovery
              ? {
                  status: row.recovery.status,
                  outcome: row.recovery.outcome,
                  targetTime: row.recovery.targetTime,
                  recoveredFrames: row.recovery.recoveredFrames,
                  requestId: row.recovery.requestId,
                  usage: row.recovery.usage,
                  timings: row.recovery.timings,
                  error: row.recovery.error ? redact(row.recovery.error) : undefined,
                }
              : undefined,
          }
        : undefined,
      track: {
        id: redact(row.trackId),
        label: redact(row.label),
        status: row.status,
        title: redact(row.title),
        detail: redact(row.detail),
        intervalId: row.intervalId ? redact(row.intervalId) : undefined,
        anchorTimes: row.anchorTimes,
        method: row.method,
        phase: row.phase,
        stage: row.stage,
        workerId: row.workerId,
      },
      activeSettings: {
        interpolationEnabled: activeSettings.interpolationEnabled,
        strategies: {
          sparse: activeSettings.strategies.sparse,
          nano: activeSettings.strategies.nano,
          hybrid: activeSettings.strategies.hybrid,
        },
        nanoMode: activeSettings.nanoMode,
        recoveryEnabled: !!activeSettings.recoveryEnabled,
      },
      frame: frame(row.frame),
      timings: row.timings
        ? {
            decodeMs: row.timings.decodeMs,
            sparseMs: row.timings.sparseMs,
            nanoMs: row.timings.nanoMs,
            nanoInitializationMs: row.timings.nanoInitializationMs,
            totalMs: row.timings.totalMs,
          }
        : undefined,
      warnings: row.warnings.map(redact),
    },
    null,
    2,
  );
}
